/**
 * Phase 101gc (Feb 2026) — Page « Tournée terrain » (AM Retrait amélioré).
 *
 * Vue tablette/mobile pour un distributeur donné, présentant une grille de
 * cartes-slots avec :
 *  - Le produit AM mappé + comparaison avec le produit Nayax (mapping check)
 *  - Le stock AM + stock MoMa attendu + delta
 *  - La DLC la plus courte + badge
 *  - Actions par slot : ← Retirer / → Rajouter (dialog unique avec 2 onglets)
 *
 * En fin de session, un bouton « Envoyer à Nayax » lance le resync (endpoint
 * `POST /distributors/{id}/nayax-resync-stock`) pour aligner MoMa sur AM.
 *
 * Route : `/tournee/:id`
 * Rôles autorisés : admin, operator, field_operator, driver
 */
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { toast } from "sonner";
import {
  Building2,
  ArrowLeft,
  RefreshCw,
  Loader2,
  ArrowDownToLine,
  AlertTriangle,
  Package,
  CheckCircle2,
  Clock,
  PackageMinus,
  PackagePlus,
  LogOut,
  Send,
  Circle,
  AlertCircle,
} from "lucide-react";
import { api } from "../lib/api";
import { useAuth } from "../lib/auth";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import FieldSlotDialog from "../components/FieldSlotDialog";
import NayaxResyncDialog from "../components/NayaxResyncDialog";

// Phase 101gc.6 — Statuts simplifiés en 3 niveaux visuels universels
// (au lieu de 6 badges peu compréhensibles) :
//   🟢 OK          : rien à faire
//   🟠 ATTENTION   : DLC courte, à surveiller
//   🔴 ACTION      : slot à réappro ou à corriger d'urgence
const STATUS_META = {
  in_sync:       { label: "OK",      level: "ok",      color: "#1F4E3D", bg: "#E8F1EA", border: "#B9D6BD", icon: "✓" },
  dlc_soon:      { label: "DLC ⚠",   level: "warn",    color: "#8C6B2F", bg: "#FBEEDB", border: "#E8D3A8", icon: "⏰" },
  moma_above_am: { label: "Corriger", level: "action", color: "#8C3A2A", bg: "#FBE5E0", border: "#E8C7BD", icon: "!" },
  moma_below_am: { label: "Réappro", level: "action", color: "#8C3A2A", bg: "#FBE5E0", border: "#E8C7BD", icon: "↑" },
  empty_am:      { label: "Vide",    level: "action", color: "#B93A25", bg: "#FDECE7", border: "#F0C8BF", icon: "∅" },
  mapping_broken:{ label: "Mapping", level: "warn",    color: "#5B3A8C", bg: "#F0E5F1", border: "#D8C0E0", icon: "?" },
};

function fmtDLC(iso) {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleDateString("fr-CH", { day: "2-digit", month: "2-digit" }); }
  catch { return iso; }
}

export default function FieldTour() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const [snapshot, setSnapshot] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [openSlot, setOpenSlot] = useState(null);
  const [sessionCounts, setSessionCounts] = useState({ withdraws: 0, adds: 0, net: 0 });

  const role = user?.role;
  const isDriver = role === "driver";

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const r = await api.get(`/distributors/${id}/field-tour/snapshot`);
      setSnapshot(r.data);
    } catch (e) {
      const detail = e?.response?.data?.detail;
      setError(typeof detail === "string" ? detail : "Erreur chargement");
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => { load(); }, [load]);

  const summary = snapshot?.summary;
  const slots = snapshot?.slots || [];
  const products = snapshot?.products || [];
  const dist = snapshot?.distributor;

  const handleSlotChange = (op, delta) => {
    setSessionCounts((prev) => ({
      withdraws: op === "withdraw" ? prev.withdraws + 1 : prev.withdraws,
      adds: op === "add" ? prev.adds + 1 : prev.adds,
      net: op === "add" ? prev.net + delta : prev.net - delta,
    }));
    load();
  };

  const hasActivity = sessionCounts.withdraws > 0 || sessionCounts.adds > 0;

  // Phase 101gc.6 — Warning à la fermeture / navigation si session non envoyée
  useEffect(() => {
    if (!hasActivity) return;
    const beforeUnload = (e) => {
      e.preventDefault();
      e.returnValue = "Tu n'as pas encore envoyé les changements à la machine. Continuer ?";
      return e.returnValue;
    };
    window.addEventListener("beforeunload", beforeUnload);
    return () => window.removeEventListener("beforeunload", beforeUnload);
  }, [hasActivity]);

  const goBack = () => {
    if (hasActivity) {
      const ok = window.confirm(
        "⚠️  Tu as des modifications non envoyées à la machine.\n\n"
        + "Si tu quittes maintenant, la machine ne sera PAS synchronisée.\n\n"
        + "Utilise le bouton « Envoyer à Nayax » avant de partir.\n\n"
        + "Quitter quand même ?"
      );
      if (!ok) return;
    }
    if (isDriver) {
      navigate("/tournee");
    } else {
      navigate(`/distributeurs/${id}`);
    }
  };

  if (loading && !snapshot) {
    return (
      <div className="min-h-screen bg-[#F5F5F0] flex items-center justify-center">
        <Loader2 className="w-6 h-6 animate-spin text-[#1F4E3D]" />
      </div>
    );
  }

  if (error && !snapshot) {
    return (
      <div className="min-h-screen bg-[#F5F5F0] p-4">
        <div className="max-w-md mx-auto mt-8 bg-white border border-[#E5E5E0] rounded-lg p-6 text-center">
          <AlertTriangle className="w-12 h-12 text-[#8C3A2A] mx-auto mb-3" />
          <p className="text-sm text-[#575A57]">{error}</p>
          <Button onClick={load} className="mt-4">Réessayer</Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F5F5F0]" data-testid="field-tour-page">
      {/* Header sticky compact */}
      <header className="sticky top-0 z-30 bg-white border-b border-[#E5E5E0] shadow-sm">
        <div className="max-w-6xl mx-auto px-3 sm:px-4 py-2.5 flex items-center gap-3">
          <Button variant="ghost" size="icon" className="w-9 h-9" onClick={goBack} data-testid="field-tour-back">
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2">
              <Building2 className="w-4 h-4 text-[#1F4E3D] shrink-0" />
              <h1 className="font-heading text-base sm:text-lg truncate">{dist?.name || "…"}</h1>
            </div>
            <div className="text-[11px] text-[#A3A6A3] flex flex-wrap gap-x-2">
              {dist?.code && <span className="font-mono">{dist.code}</span>}
              {dist?.city && <span>· {dist.city}</span>}
              {snapshot?.moma_snapshot_at && (
                <span className="flex items-center gap-0.5"><Clock className="w-2.5 h-2.5" /> Nayax {new Date(snapshot.moma_snapshot_at).toLocaleTimeString("fr-CH", { hour: "2-digit", minute: "2-digit" })}</span>
              )}
            </div>
          </div>
          <Button
            variant="outline"
            size="sm"
            onClick={load}
            disabled={loading}
            className="h-9 shrink-0"
            data-testid="field-tour-refresh"
          >
            <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} />
          </Button>
          {isDriver && (
            <Button
              variant="ghost"
              size="sm"
              onClick={logout}
              className="h-9 shrink-0 text-[#8C3A2A] hover:bg-[#FBE5E0]"
              data-testid="field-tour-logout"
              title="Déconnexion"
            >
              <LogOut className="w-4 h-4" />
            </Button>
          )}
        </div>

        {/* Bandeau session en cours */}
        {hasActivity && (
          <div className="bg-[#1F4E3D] text-white px-3 sm:px-4 py-2 text-xs sm:text-sm flex items-center justify-between gap-3 max-w-6xl mx-auto" data-testid="field-tour-session-bar">
            <div className="flex items-center gap-3 flex-wrap">
              <span><strong>{sessionCounts.withdraws}</strong> retrait{sessionCounts.withdraws !== 1 ? "s" : ""}</span>
              <span>·</span>
              <span><strong>{sessionCounts.adds}</strong> ajout{sessionCounts.adds !== 1 ? "s" : ""}</span>
              <span>·</span>
              <span>Net : <strong>{sessionCounts.net >= 0 ? "+" : ""}{sessionCounts.net}</strong> barq.</span>
            </div>
            {dist?.nayax_machine_id && (
              <NayaxResyncDialog
                distributorId={id}
                distributorName={dist?.name || ""}
                onDone={() => {
                  setSessionCounts({ withdraws: 0, adds: 0, net: 0 });
                  load();
                }}
              />
            )}
          </div>
        )}
      </header>

      <div className="max-w-6xl mx-auto px-3 sm:px-4 py-4 pb-24 space-y-4">
        {/* Résumé simplifié à 3 chips visuels (au lieu de 6) */}
        {summary && (
          <div className="grid grid-cols-3 gap-2">
            <SummaryChip
              label="Tout OK"
              value={summary.in_sync}
              bg="#E8F1EA"
              text="#1F4E3D"
              testid="ft-summary-in-sync"
              icon="✓"
            />
            <SummaryChip
              label="À surveiller"
              value={(summary.dlc_soon || 0) + (summary.mapping_broken || 0)}
              bg="#FBEEDB"
              text="#8C6B2F"
              testid="ft-summary-warn"
              icon="⏰"
            />
            <SummaryChip
              label="À faire"
              value={(summary.moma_above_am || 0) + (summary.moma_below_am || 0) + (summary.empty_am || 0)}
              bg="#FBE5E0"
              text="#8C3A2A"
              testid="ft-summary-action"
              icon="!"
            />
          </div>
        )}

        {/* Grille de slots */}
        {slots.length === 0 ? (
          <div className="bg-white border border-dashed border-[#E5E5E0] rounded-lg p-8 text-center text-sm text-[#A3A6A3]">
            Aucun slot configuré pour ce distributeur.
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3" data-testid="field-tour-slots-grid">
            {slots.map((slot) => (
              <SlotCard key={slot.slot_number} slot={slot} onClick={() => setOpenSlot(slot)} />
            ))}
          </div>
        )}
      </div>

      {/* Phase 101gc.6 — Bouton flottant PERMANENT « Envoyer à MoMa »
          toujours visible en bas d'écran pour ne PAS l'oublier.
          Devient rouge + pulse dès qu'une session est en cours. */}
      {dist?.nayax_machine_id && (
        <div className="fixed bottom-0 left-0 right-0 z-40 bg-white border-t border-[#E5E5E0] shadow-[0_-2px_8px_rgba(0,0,0,0.06)] p-3">
          <div className="max-w-6xl mx-auto flex items-center justify-between gap-3">
            <div className="flex-1 text-xs sm:text-sm">
              {hasActivity ? (
                <div className="text-[#8C3A2A] font-medium flex items-center gap-1.5" data-testid="fab-pending-hint">
                  <span className="w-2 h-2 bg-[#8C3A2A] rounded-full animate-pulse" />
                  N'oublie pas d'envoyer à la machine
                </div>
              ) : (
                <div className="text-[#575A57]">
                  Fini ? Envoie la mise à jour à la machine 👉
                </div>
              )}
            </div>
            <NayaxResyncDialog
              distributorId={id}
              distributorName={dist?.name || ""}
              onDone={() => {
                setSessionCounts({ withdraws: 0, adds: 0, net: 0 });
                load();
              }}
            />
          </div>
        </div>
      )}

      {/* Dialog slot */}
      {openSlot && (
        <FieldSlotDialog
          open={!!openSlot}
          onOpenChange={(v) => { if (!v) setOpenSlot(null); }}
          slot={openSlot}
          products={products}
          distributorId={id}
          onDone={(payload) => {
            if (payload?.op) {
              handleSlotChange(payload.op, payload.qty || 0);
            } else {
              load();
            }
            setOpenSlot(null);
          }}
        />
      )}
    </div>
  );
}

function SummaryChip({ label, value, bg, text, testid, icon }) {
  return (
    <div
      className="rounded-lg px-3 py-2 text-center border-2"
      style={{ backgroundColor: bg, borderColor: bg, color: text }}
      data-testid={testid}
    >
      <div className="flex items-center justify-center gap-1 text-lg leading-none">
        {icon && <span>{icon}</span>}
        <span className="text-2xl font-heading font-bold">{value ?? 0}</span>
      </div>
      <div className="text-[10px] uppercase tracking-wider font-semibold mt-1 opacity-80">{label}</div>
    </div>
  );
}

function SlotCard({ slot, onClick }) {
  const meta = STATUS_META[slot.status] || STATUS_META.in_sync;
  const hasProduct = !!slot.product_am;
  const dlcDays = slot.dlc_shortest ? Math.floor((new Date(slot.dlc_shortest) - Date.now()) / 86400000) : null;

  return (
    <button
      type="button"
      onClick={onClick}
      className="text-left bg-white border-2 rounded-lg p-3 hover:shadow-md transition-all active:scale-[0.98]"
      style={{ borderColor: meta.border }}
      data-testid={`field-tour-slot-${slot.slot_number}`}
    >
      <div className="flex items-start justify-between mb-2">
        <div className="flex items-center gap-2">
          <span
            className="inline-flex items-center justify-center bg-[#1F4E3D] text-white rounded-md w-9 h-9 font-mono text-base font-semibold shrink-0"
          >
            {slot.slot_number}
          </span>
        </div>
        {/* Grosse pastille visuelle avec icône + libellé */}
        <div
          className="flex items-center gap-1 px-2 py-1 rounded-full text-xs font-semibold"
          style={{ backgroundColor: meta.bg, color: meta.color }}
        >
          <span className="text-sm leading-none">{meta.icon}</span>
          <span>{meta.label}</span>
        </div>
      </div>

      <div className="mb-2">
        {hasProduct ? (
          <>
            <div className="font-medium text-sm text-[#1A1C1A] truncate">{slot.product_am.name}</div>
            <div className="text-[10px] text-[#A3A6A3] flex gap-1.5 mt-0.5">
              {slot.product_am.sku && <span className="font-mono">{slot.product_am.sku}</span>}
              {slot.product_am.detected_from_lots && <span className="text-[#8C6B2F]">· détecté</span>}
              {slot.product_am.detected_from_nayax && <span className="text-[#8C6B2F]">· via Nayax</span>}
            </div>
          </>
        ) : (
          <div className="text-sm text-[#A3A6A3] italic">Slot non mappé</div>
        )}
      </div>

      {/* Compteurs très gros et lisibles au premier coup d'oeil */}
      <div className="grid grid-cols-3 gap-1.5 text-xs">
        <div className="bg-[#F5F9F6] border border-[#B9D6BD] rounded p-1.5 text-center">
          <div className="text-[9px] uppercase text-[#1F4E3D]">AM</div>
          <div className="font-mono font-bold text-lg text-[#1F4E3D] leading-none">{slot.am_stock}</div>
        </div>
        <div className="bg-[#F5F9F6] border border-[#B9D6BD] rounded p-1.5 text-center">
          <div className="text-[9px] uppercase text-[#1F4E3D]">MACHINE</div>
          <div className="font-mono font-bold text-lg text-[#1F4E3D] leading-none">
            {slot.moma_stock === null || slot.moma_stock === undefined ? "—" : slot.moma_stock}
          </div>
        </div>
        <div className="bg-[#FAFAF7] border border-[#E5E5E0] rounded p-1.5 text-center">
          <div className="text-[9px] uppercase text-[#575A57]">DLC</div>
          <div className={`font-mono font-semibold ${dlcDays !== null && dlcDays <= 2 ? "text-[#8C6B2F]" : "text-[#575A57]"}`}>
            {slot.dlc_shortest ? fmtDLC(slot.dlc_shortest) : "—"}
          </div>
        </div>
      </div>

      <div className="mt-2 pt-2 border-t border-[#F0F0EB] flex items-center justify-between text-[10px] text-[#575A57]">
        <span className="flex items-center gap-0.5"><PackageMinus className="w-3 h-3" /> Retirer</span>
        <span className="text-[#A3A6A3]">Toucher pour agir</span>
        <span className="flex items-center gap-0.5"><PackagePlus className="w-3 h-3" /> Rajouter</span>
      </div>
    </button>
  );
}
